<template>
  <article class="reward-card">
    <div class="head">{{ card.icon }} {{ card.label }}</div>
    <div class="amount">{{ formattedAmount }}</div>
    <button
      type="button"
      :class="{ 'is-claimed': card.claimed }"
      :disabled="!card.claimable"
      @click="emit('claim')"
    >
      <!-- 未开放且有 next_at：一天内倒计时，更久显示本地开放时刻（设备时区） -->
      <template v-if="showOpens">
        <template v-if="opensWithinDay && opens.remaining.value !== null">
          {{
            t('promo.vip.action.LOCKED_COUNTDOWN', { countdown: countdown(opens.remaining.value) })
          }}
        </template>
        <i18n-t v-else keypath="promo.vip.action.LOCKED_WITH_DATE" tag="span" scope="global">
          <template #date><LocalTime :value="card.nextAt" /></template>
        </i18n-t>
      </template>
      <template v-else>{{ card.actionText }}</template>
    </button>
    <!-- 可领且有 expire_at：一天内倒计时，更久显示本地过期时刻 -->
    <p v-if="showExpires" class="expire">
      <template v-if="expiresWithinDay && expires.remaining.value !== null">
        ⏳ {{ t('common.time.expiresIn', { countdown: countdown(expires.remaining.value) }) }}
      </template>
      <i18n-t v-else keypath="common.time.expiresAt" tag="span" scope="global">
        <template #date><LocalTime :value="card.expireAt" /></template>
      </i18n-t>
    </p>
  </article>
</template>

<script setup lang="ts">
import { formatBaseAmt } from '~/utils/transfer/money'
import { DAY_MS } from '~/utils/time'
import type { VipRewardCard } from '../types'

const props = defineProps<{ card: VipRewardCard }>()
/** refresh：开放 / 过期时刻到点，由父级重新拉俸禄面板（不在本地改状态） */
const emit = defineEmits<{ claim: []; refresh: [] }>()

const { t } = useI18n()
const userStore = useUserStore()
const { countdown } = useTimeDisplay()
const formattedAmount = computed(() => formatBaseAmt(props.card.amount, userStore.baseCurrency))

const showOpens = computed(() => props.card.state === 'LOCKED' && !!props.card.nextAt)
const showExpires = computed(() => props.card.claimable && !!props.card.expireAt)

const opens = useCountdown(() => (showOpens.value ? props.card.nextAt : null), {
  onExpire: () => emit('refresh'),
})
const expires = useCountdown(() => (showExpires.value ? props.card.expireAt : null), {
  onExpire: () => emit('refresh'),
})

const opensWithinDay = computed(
  () => opens.remaining.value !== null && opens.remaining.value <= DAY_MS,
)
const expiresWithinDay = computed(
  () => expires.remaining.value !== null && expires.remaining.value <= DAY_MS,
)
</script>

<style scoped lang="scss">
.reward-card {
  background: var(--surface);

  border: 1px solid var(--hairline);

  border-radius: 14px;

  padding: 13px 14px;

  animation: promo-rise 0.28s ease-out backwards;
  animation-delay: calc(var(--i, 0) * 40ms);
}

.head {
  font-size: 13px;

  font-weight: 700;
}

.head .icon {
  font-size: 15px;
}

.reward-card .amount {
  font-size: 22px;

  font-weight: 900;

  color: var(--primary);

  margin: 6px 0 10px;
}

.reward-card button {
  width: 100%;

  padding: 8px;

  border-radius: var(--r-sm);

  background: var(--primary);

  color: #fff;

  font-size: 13px;

  font-weight: 800;
}

.reward-card button:disabled {
  background: var(--surface2);

  color: var(--muted);
}

.reward-card button:is(.is-claimed) {
  background: transparent;

  color: var(--success);

  border: 1px solid rgba(40, 209, 124, 0.4);

  opacity: 0.55;
}

.reward-card .expire {
  margin-top: 6px;
  font-size: 11px;
  color: var(--muted);
  font-variant-numeric: tabular-nums;
}
</style>
